import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { BankHolidayORT, Ort } from '@types';
import _ from 'lodash';
import { useMemo } from 'react';
import { translateDaysToDutch, weekDays } from '../../../../../../shared/utils/date-utils';

type CompanyDetailOrtsProps = {
  weekdayOrts: Ort | undefined;
  bankHolidayOrts: BankHolidayORT | undefined;
  type: 'Uitzendkracht' | 'Zelfstandige';
};

function ortLabel({
  surcharge,
  surchargeType,
}: {
  surcharge: number;
  surchargeType: 'factor' | 'offset';
}) {
  if (surcharge == null) {
    return 'Geen';
  }
  if (surchargeType === 'factor') {
    return `${surcharge}% ORT`;
  } else {
    return `€${surcharge.toFixed(2)} ORT`;
  }
}

const CompanyDetailOrts = ({ weekdayOrts, bankHolidayOrts, type }: CompanyDetailOrtsProps) => {
  return (
    <Box role="tabpanel" pb={4}>
      <Typography variant="h6">{type}</Typography>
      <CompanyDetailWeekdayOrts weekdayOrts={weekdayOrts} type={type} />
      <CompanyDetailBankHolidayOrts bankHolidayOrts={bankHolidayOrts} type={type} />
    </Box>
  );
};

const CompanyDetailWeekdayOrts = ({
  weekdayOrts,
  type,
}: Pick<CompanyDetailOrtsProps, 'weekdayOrts' | 'type'>) => {
  return (
    <OrtCalendarGrid container columns={7} data-testid={`weekdays-calendar-${type.toLowerCase()}`}>
      {weekDays.map((weekDay) => (
        <Grid key={weekDay} item xs={1}>
          <OrtWeekDayGrid container>
            <HeaderGridItem item>
              <HeaderTypography color="secondary" variant="subtitle2">
                {translateDaysToDutch(weekDay)}
              </HeaderTypography>
            </HeaderGridItem>
            {weekdayOrts && weekdayOrts.calendar
              ? weekdayOrts.calendar.workDays[weekDay].map((ortPeriod) => (
                  <OrtWeekDayGridItem item key={ortPeriod.startTime}>
                    <Typography variant="body1">
                      {DateTime.fromFormat(ortPeriod.startTime, 'HH:mm:ss').toFormat('HH:mm')} -{' '}
                      {DateTime.fromFormat(ortPeriod.endTime, 'HH:mm:ss').toFormat('HH:mm')}
                    </Typography>
                    <Typography variant="body1">
                      {ortLabel({
                        surcharge: ortPeriod.surcharge ?? 0,
                        surchargeType: ortPeriod.surchargeType,
                      })}
                    </Typography>
                  </OrtWeekDayGridItem>
                ))
              : undefined}
          </OrtWeekDayGrid>
        </Grid>
      ))}
    </OrtCalendarGrid>
  );
};

const CompanyDetailBankHolidayOrts = ({
  bankHolidayOrts,
  type,
}: Pick<CompanyDetailOrtsProps, 'bankHolidayOrts' | 'type'>) => {
  const chunks = useMemo(() => {
    const bankHolidays = bankHolidayOrts
      ? Object.entries(bankHolidayOrts).map(([name, ortPeriod]) => ({
          name,
          ort: ortPeriod,
        }))
      : [];
    const chunks = _.chunk(bankHolidays, 7);
    return chunks;
  }, [bankHolidayOrts]);

  return (
    <Box data-testid={`bank-holidays-calendar-${type.toLowerCase()}`}>
      {chunks.map((bankHolidayChunk, index) => {
        return (
          <OrtCalendarGrid key={index} container columns={7} mt={16}>
            {bankHolidayChunk.map((bankHolidays) => {
              const name = bankHolidays.name;
              return (
                <Grid key={name} item xs={1}>
                  <OrtWeekDayGrid container>
                    <HeaderGridItem item>
                      <HeaderTypography color="secondary" variant="subtitle2" noWrap title={name}>
                        {name}
                      </HeaderTypography>
                    </HeaderGridItem>
                    {bankHolidays.ort.map((bankHoliday) => {
                      return (
                        <OrtWeekDayGridItem
                          key={`${bankHoliday.startTime}-${bankHoliday.endTime}`}
                          item
                        >
                          <Typography variant="body1">
                            {DateTime.fromFormat(bankHoliday.startTime, 'HH:mm:ss').toFormat(
                              'HH:mm'
                            )}
                            -
                            {DateTime.fromFormat(bankHoliday.endTime, 'HH:mm:ss').toFormat('HH:mm')}
                          </Typography>
                          {bankHoliday.surcharge != null ? (
                            <Typography variant="body1">
                              {ortLabel({
                                surcharge: bankHoliday.surcharge ?? 0,
                                surchargeType: bankHoliday.surchargeType,
                              })}
                            </Typography>
                          ) : (
                            <Typography variant="body1">Regular</Typography>
                          )}
                        </OrtWeekDayGridItem>
                      );
                    })}
                  </OrtWeekDayGrid>
                </Grid>
              );
            })}
          </OrtCalendarGrid>
        );
      })}
    </Box>
  );
};

const HeaderGridItem = styled(Grid)(({ theme }) => ({
  width: '90%',
  padding: theme.spacing(2),
  backgroundColor: 'transparent',
  border: 'none',
  textAlign: 'center',
}));

const HeaderTypography = styled(Typography)(() => ({
  fontWeight: 'bold',
  textTransform: 'capitalize',
  textAlign: 'center',
  color: 'black',
}));

const OrtCalendarGrid = styled(Grid)(({ theme }) => ({
  borderColor: theme.palette.grey[200],
}));

const OrtWeekDayGrid = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(1),
}));

const OrtWeekDayGridItem = styled(Grid)(({ theme }) => ({
  width: '90%',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderLeft: `2px solid ${theme.palette.lwPrimaryAccentLight['10']}`,
  borderRadius: theme.spacing(1),
}));

export { CompanyDetailOrts };
